import { Alert, Popover, Portal } from '@chakra-ui/react';
import { type ReactNode, useState } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';

/**
 * Displays the error message in a format suitable for form fields, without
 * breaking layout:
 * - truncate error.message
 * - display the complete message in a popover
 */
export const ErrorFallback = (props: FallbackProps) => {
  const { error } = props as { error: Error };
  const [open, setOpen] = useState(false);
  return (
    <Alert.Root status="error" variant="subtle">
      <Alert.Content>
        <Alert.Title>Error</Alert.Title>

        {error?.message && (
          <Popover.Root
            open={open}
            lazyMount
            positioning={{ strategy: 'fixed' }}
          >
            <Popover.Trigger asChild>
              <Alert.Description
                layerStyle="truncate"
                cursor="help"
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
              >
                {error.message}
              </Alert.Description>
            </Popover.Trigger>

            <Portal>
              <Popover.Positioner>
                <Popover.Content
                  whiteSpace="break-spaces"
                  fontFamily="monospace"
                >
                  <Popover.Arrow />
                  <Popover.Body>{error.toString()}</Popover.Body>
                </Popover.Content>
              </Popover.Positioner>
            </Portal>
          </Popover.Root>
        )}
      </Alert.Content>
    </Alert.Root>
  );
};

export type DefaultErrorBoundaryProps = {
  children: ReactNode;
};

/** @see https://github.com/bvaughn/react-error-boundary */
export function DefaultErrorBoundary({ children }: DefaultErrorBoundaryProps) {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>{children}</ErrorBoundary>
  );
}
